import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Search } from "@mui/icons-material";
import { Box, TextField } from "@mui/material";
import { ColDef, GridOptions, RowDragEndEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import analytics from "fond/analytics";
import { selectAllAccounts, selectCurrentAccount, selectExternalAccounts } from "fond/api";
import { useAccountRoles } from "fond/hooks/useAccountRoles";
import { useAccountSwitching } from "fond/hooks/useAccountSwitching";
import { useContents } from "fond/hooks/useContents";
import { updateFilter } from "fond/projects/redux";
import { Account, AccountBase } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { AgGrid } from "fond/widgets";
import { NavButton } from "fond/widgets/Link/NavButton";

import { usePinned } from "./hooks/usePinned";
import PinCellRenderer from "./PinCellRenderer";
import SwitchAccountCellRenderer from "./SwitchAccountCellRenderer";
import TypeCellRenderer from "./TypeCellRenderer";

const gridOptions: GridOptions = {
  defaultColDef: {
    resizable: false,
    sortable: false,
  },
  domLayout: "autoHeight",
  animateRows: true,
  suppressGroupRowsSticky: true,
  suppressMenuHide: false,
  suppressContextMenu: true,
  sideBar: false,
  rowGroupPanelShow: "never",
  getRowId: (params) => params.data.ID,
  rowDragManaged: true,
  rowDragEntireRow: true,
};
type AccountWithType = AccountBase & { accountType: "Admin" | "Member" | "Guest" | "External" };

/**
 * A list of all accounts that the user is a member of or guest in.
 */
const AccountList: React.FC = () => {
  useContents();
  const memberAccounts = useSelector(selectAllAccounts);
  const externalAccounts = useSelector(selectExternalAccounts);
  const currentAccount = useSelector(selectCurrentAccount);
  const accountRoles = useAccountRoles();
  const { pinned, addToPinned, removeFromPinned, setPinnedOrder, saving } = usePinned();
  const { initiateSwitch, modal: accountSwitchingModal } = useAccountSwitching();
  const gridRef = useRef<AgGridReact>(null);
  const [accountSearch, setAccountSearch] = useState("");
  const dispatch = useAppDispatch();

  // Clear filter
  useEffect(() => {
    dispatch(updateFilter(null));
  }, [dispatch]);

  // Set grid loading state based on saving state
  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", saving);
  }, [saving]);

  const handleOnFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAccountSearch(event.target.value);
    gridRef.current?.api?.setGridOption("quickFilterText", event.target.value ?? "");
  };

  // Combine member and external accounts with type indicator and sort by pinned order
  // Note: we cannot let AgGrid filter by pinned as it will disable row dragging.
  const allAccounts: AccountWithType[] = useMemo(() => {
    const members: AccountWithType[] = memberAccounts.map((account) => {
      const role = accountRoles.get(account.ID);
      let accountType: AccountWithType["accountType"] = "Member";
      if (role === "admin") accountType = "Admin";
      else if (role === "guest") accountType = "Guest";
      return { ...account, accountType };
    });
    const externals: AccountWithType[] = externalAccounts.map((account) => ({ ...account, accountType: "External" as const }));
    const combined = [...members, ...externals];

    // Sort accounts: pinned accounts first in their saved order, then unpinned accounts
    return combined.sort((a, b) => {
      const aIndex = pinned.indexOf(a.ID);
      const bIndex = pinned.indexOf(b.ID);
      const aPinned = aIndex !== -1;
      const bPinned = bIndex !== -1;

      // Both pinned: sort by pinned order
      if (aPinned && bPinned) {
        return aIndex - bIndex;
      }
      // Only a is pinned: a comes first
      if (aPinned) return -1;
      // Only b is pinned: b comes first
      if (bPinned) return 1;
      // Neither pinned: maintain original order
      return 0;
    });
  }, [memberAccounts, externalAccounts, accountRoles, pinned]);

  const isPinned = useCallback((id?: string) => (id ? pinned.includes(id) : false), [pinned]);

  // Handle row drag end to update pinned order
  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent) => {
      // Get current row order from grid after drag
      const rowData: AccountWithType[] = [];
      gridRef.current?.api?.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) {
          rowData.push(node.data);
        }
      });

      // Update pinned order based on new grid order
      const newPinnedOrder = rowData.filter((acc) => isPinned(acc.ID)).map((acc) => acc.ID);
      if (newPinnedOrder.length > 0) {
        setPinnedOrder(newPinnedOrder);
        analytics.track("account.reordered", {
          pinnedCount: newPinnedOrder.length,
        });
      }
    },
    [isPinned, setPinnedOrder]
  );

  // Add row drag end event listener
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) {
      api.addEventListener("rowDragEnd", handleRowDragEnd);
      return () => {
        api.removeEventListener("rowDragEnd", handleRowDragEnd);
      };
    }
    return undefined;
  }, [handleRowDragEnd]);

  const handleTogglePin = useCallback(
    (account: Account) => {
      if (isPinned(account.ID)) {
        removeFromPinned(account);
      } else {
        addToPinned(account);
      }
    },
    [isPinned, addToPinned, removeFromPinned]
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "pin",
        headerName: "",
        width: 85,
        cellRenderer: PinCellRenderer,
        cellRendererParams: {
          isPinned,
          onTogglePin: handleTogglePin,
        },
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        rowDrag: (params) => isPinned(params.data?.ID),
      },
      {
        headerName: "Account Name",
        field: "Name",
        flex: 2,
        suppressHeaderMenuButton: true,
        resizable: false,
      },
      {
        headerName: "Type",
        field: "accountType",
        width: 125,
        cellRenderer: TypeCellRenderer,
        suppressHeaderMenuButton: true,
        resizable: false,
      },
      {
        headerName: "",
        field: "switch",
        width: 100,
        cellRenderer: SwitchAccountCellRenderer,
        cellRendererParams: {
          onSwitchAccount: initiateSwitch,
          currentAccountId: currentAccount?.ID,
        },
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
      },
    ],
    [isPinned, handleTogglePin, initiateSwitch, currentAccount?.ID]
  );

  return (
    <>
      <Box className="account-list" data-testid="account-list" sx={{ display: "flex", flexGrow: 1, flexDirection: "column" }}>
        {memberAccounts.length >= 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <NavButton
              onClick={() => analytics.track("topbar.settings.menu.switch-account.create-account.clicked")}
              to="/createaccount"
              sx={{ px: 4 }}
            >
              Create Account
            </NavButton>
          </Box>
        )}
        <TextField
          id="account-search-field"
          name="search"
          size="small"
          placeholder="Filter by name"
          value={accountSearch}
          onChange={handleOnFilterChange}
          InputProps={{ startAdornment: <Search /> }}
        />

        <Box flexGrow={1} mt={2}>
          <AgGrid
            ref={gridRef}
            gridOptions={gridOptions}
            rowData={allAccounts}
            columnDefs={columnDefs}
            containerProps={{ flexGrow: 1 }}
            variant="outlined"
            size="large"
          />
        </Box>
      </Box>
      {accountSwitchingModal}
    </>
  );
};

export default AccountList;
